import type { Database } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';
import { escapeIlikeTerm } from '../ilike';

export type AdminUserRow = Pick<
  Database['public']['Tables']['users']['Row'],
  | 'id'
  | 'email'
  | 'role'
  | 'status'
  | 'first_name'
  | 'last_name'
  | 'phone'
  | 'language_preference'
  | 'auth_provider'
  | 'is_root'
  | 'last_sign_in_at'
  | 'created_at'
  | 'deleted_at'
>;

export type AdminUserStatusFilter = 'all' | 'active' | 'disabled';
export type AdminUserRoleFilter = 'all' | 'buyer' | 'admin' | 'super_admin';

export type ListAdminUsersParams = {
  status?: AdminUserStatusFilter;
  role?: AdminUserRoleFilter;
  searchQuery?: string;
  pageSize?: number;
  offset?: number;
};

export type ListAdminUsersResult = {
  rows: AdminUserRow[];
  total: number;
};

const DEFAULT_PAGE_SIZE = 25;

const SELECT_USER_COLUMNS =
  'id, email, role, status, first_name, last_name, phone, language_preference, auth_provider, is_root, last_sign_in_at, created_at, deleted_at';

/**
 * List admin-visible users with role/status filtering. Pagination via
 * `offset` + `pageSize`. Free-text search matches email / first_name /
 * last_name (case-insensitive substring).
 *
 * Authorization: relies on the `users_select` RLS policy from
 * 002_users.sql §3 — admins see buyers + themselves, super_admins see
 * everyone. The front does NOT filter by role at the query level: RLS
 * already enforces the matrix R2.
 */
export async function listAdminUsers(
  params: ListAdminUsersParams = {},
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<ListAdminUsersResult> {
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const offset = params.offset ?? 0;

  let q = client.from('users').select(SELECT_USER_COLUMNS, { count: 'exact' });

  if (params.status === 'active') {
    q = q.eq('status', 'active').is('deleted_at', null);
  } else if (params.status === 'disabled') {
    q = q.eq('status', 'disabled');
  }

  if (params.role && params.role !== 'all') {
    q = q.eq('role', params.role);
  }

  if (params.searchQuery && params.searchQuery.trim().length > 0) {
    const term = escapeIlikeTerm(params.searchQuery.trim());
    if (term.length > 0) {
      q = q.or(`email.ilike.%${term}%,first_name.ilike.%${term}%,last_name.ilike.%${term}%`);
    }
  }

  const { data, error, count } = await q
    .order('created_at', { ascending: false })
    .range(offset, offset + pageSize - 1);

  if (error) throw error;
  return {
    rows: (data ?? []) as AdminUserRow[],
    total: count ?? 0,
  };
}

export type UserRole = 'buyer' | 'admin' | 'super_admin';

export async function promoteUser(
  userId: string,
  newRole: UserRole,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminUserRow> {
  const { data, error } = await client
    .from('users')
    .update({ role: newRole })
    .eq('id', userId)
    .select(SELECT_USER_COLUMNS)
    .single();

  if (error) throw error;
  return data as AdminUserRow;
}

export async function disableUser(
  userId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminUserRow> {
  const { data, error } = await client
    .from('users')
    .update({ status: 'disabled' as const })
    .eq('id', userId)
    .select(SELECT_USER_COLUMNS)
    .single();

  if (error) throw error;
  return data as AdminUserRow;
}

export async function reactivateUser(
  userId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminUserRow> {
  const { data, error } = await client
    .from('users')
    .update({ status: 'active' as const })
    .eq('id', userId)
    .select(SELECT_USER_COLUMNS)
    .single();

  if (error) throw error;
  return data as AdminUserRow;
}
